import { Component, forwardRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AccountGroup } from '@galaxy/account-group';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Order } from '@vendasta/sales-orders';
import { User } from '@vendasta/store';
import { WorkOrderPersona } from '@vendasta/work-order';
import { combineLatest, Observable, of, Subscription, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap } from 'rxjs/operators';
import { OrderStoreService } from '../../core/orders.service';
import { Badge, badgeWithOrderStatus } from '../../core/status';
import { ORDER_FORM_FILE_UPLOAD_URL } from '../../utils';
import { EditOrderComponent } from '../edit-order/edit-order.component';
import { ViewOrderComponent } from '../view-order/view-order.component';

@Component({
  selector: 'orders-view-or-edit-order',
  templateUrl: './view-or-edit-order.component.html',
  styleUrls: ['./view-or-edit-order.component.scss'],
  standalone: false,
})
export class ViewOrEditOrderComponent implements OnInit, OnDestroy {
  @Input() hideNotesSections?: boolean;
  @Input() hideTagsSection?: boolean;
  @Input() hideAttachmentsSection?: boolean;
  @Input() hideOrderFormSaveButton?: boolean;
  @Input() workOrderPersona?: WorkOrderPersona = WorkOrderPersona.SALES;
  @Input() fileUploadUrl?: string = ORDER_FORM_FILE_UPLOAD_URL;

  business$: Observable<AccountGroup>;
  order$: Observable<Order>;
  users$: Observable<User[]>;
  productIds$: Observable<string[]>;
  loading$: Observable<boolean>;

  statusBadge: Badge;

  private readonly subscriptions: Subscription[] = [];

  @ViewChild(forwardRef(() => ViewOrderComponent)) viewOrderComponent: ViewOrderComponent;
  @ViewChild('editOrder') editOrderComponent: EditOrderComponent;

  constructor(
    public readonly orderService: OrderStoreService,
    private readonly snackbarService: SnackbarService,
  ) {}

  ngOnInit(): void {
    this.business$ = this.orderService.business$.pipe(shareReplay(1));
    this.order$ = this.orderService.order$.pipe(
      catchError((error) => {
        this.snackbarService.openErrorSnack('An error occurred getting the order. Please try again');
        return throwError(error);
      }),
      tap((order: Order) => {
        this.editOrderComponent?.salesOrderForm?.refresh(order);
        this.statusBadge = badgeWithOrderStatus(order.status);
      }),
      shareReplay(1),
    );

    this.users$ = this.orderService.businessUsers$.pipe(shareReplay(1));
    this.productIds$ = this.orderService.productIds$.pipe(shareReplay(1));
    this.loading$ = combineLatest([this.order$, this.business$, this.users$, this.productIds$]).pipe(map(() => false));
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  validateForm(): Observable<boolean> {
    if (this.viewOrderComponent) {
      return of(true);
    }
    return this.editOrderComponent?.validateForm() || of(false);
  }

  validateHiddenRequiredFields(): Observable<boolean> {
    if (this.viewOrderComponent) {
      return of(true);
    }
    return this.editOrderComponent?.validateHiddenRequiredFields() || of(false);
  }

  refreshOrderDetails(): void {
    this.orderService.reloadOrder();
  }

  saveOrderFormIfDirty(): Observable<boolean> {
    return this.editOrderComponent.saveOrderFormIfDirty();
  }
}
